import React, { useState, useMemo } from 'react';
import './ReinsuranceTreaty.css';

const MOCK_TREATIES = [
  { id: 1, name: 'Cyber QS 2026', type: 'QS', reinsurer: 'Swiss Re', retention: 250000, cededPct: 75, effectiveStart: '2026-01-01', effectiveEnd: '2026-12-31' },
  { id: 2, name: 'Tech E&O XOL Layer 1', type: 'XOL', reinsurer: 'Munich Re', retention: 1000000, cededPct: 80, effectiveStart: '2026-01-01', effectiveEnd: '2026-12-31' },
  { id: 3, name: 'Cyber & Privacy QS', type: 'QS', reinsurer: 'Lloyd\'s Syndicate 1234', retention: 500000, cededPct: 70, effectiveStart: '2026-01-15', effectiveEnd: '2026-12-31' },
  { id: 4, name: 'Cyber XOL Excess Layer', type: 'XOL', reinsurer: 'AXA XL', retention: 5000000, cededPct: 90, effectiveStart: '2026-02-01', effectiveEnd: '2027-01-31' },
];

const MOCK_PREMIUM_SUMMARY = {
  grossWritten: 542200,
  ceded: 389584,
  netRetained: 152616,
};

const MOCK_BORDEREAUX = [
  { id: 1, treaty: 'Cyber QS 2026', period: 'Jan 2026', status: 'submitted', dueDate: '2026-02-28' },
  { id: 2, treaty: 'Tech E&O XOL Layer 1', period: 'Jan 2026', status: 'draft', dueDate: '2026-02-28' },
  { id: 3, treaty: 'Cyber & Privacy QS', period: 'Jan 2026', status: 'approved', dueDate: '2026-02-28' },
  { id: 4, treaty: 'Cyber XOL Excess Layer', period: 'Feb 2026', status: 'pending', dueDate: '2026-03-15' },
];

const MOCK_PANEL = [
  { reinsurer: 'Swiss Re', sharePct: 35, premium: 189805 },
  { reinsurer: 'Munich Re', sharePct: 30, premium: 162661 },
  { reinsurer: 'Lloyd\'s Syndicate 1234', sharePct: 25, premium: 135551 },
  { reinsurer: 'AXA XL', sharePct: 10, premium: 54217 },
];

function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getBordereauStatusClass(status) {
  switch (status?.toLowerCase()) {
    case 'approved': return 'ret__bord-status ret__bord-status--approved';
    case 'submitted': return 'ret__bord-status ret__bord-status--submitted';
    case 'pending': return 'ret__bord-status ret__bord-status--pending';
    case 'draft': return 'ret__bord-status ret__bord-status--draft';
    default: return 'ret__bord-status';
  }
}

export function ReinsuranceTreaty() {
  const [selectedTreaty, setSelectedTreaty] = useState(null);

  const premiumSummary = useMemo(() => MOCK_PREMIUM_SUMMARY, []);

  return (
    <div className="ret">
      <h1 className="ret__title">Reinsurance Treaties</h1>

      {/* Premium summary */}
      <div className="ret__premium-summary">
        <div className="ret__premium-card card">
          <span className="ret__premium-label">Gross Written</span>
          <span className="ret__premium-value">{formatCurrency(premiumSummary.grossWritten)}</span>
        </div>
        <div className="ret__premium-card card">
          <span className="ret__premium-label">Ceded</span>
          <span className="ret__premium-value ret__premium-value--secondary">{formatCurrency(premiumSummary.ceded)}</span>
        </div>
        <div className="ret__premium-card card">
          <span className="ret__premium-label">Net Retained</span>
          <span className="ret__premium-value ret__premium-value--green">{formatCurrency(premiumSummary.netRetained)}</span>
        </div>
      </div>

      {/* Treaty cards */}
      <div className="ret__section">
        <h2 className="ret__section-title">Treaty Overview</h2>
        <div className="ret__treaty-grid">
          {MOCK_TREATIES.map((t) => (
            <div
              key={t.id}
              className={`ret__treaty-card card ${selectedTreaty === t.id ? 'ret__treaty-card--selected' : ''}`}
              onClick={() => setSelectedTreaty(selectedTreaty === t.id ? null : t.id)}
            >
              <div className="ret__treaty-header">
                <h3 className="ret__treaty-name">{t.name}</h3>
                <span className={`ret__treaty-type ret__treaty-type--${t.type.toLowerCase()}`}>
                  {t.type}
                </span>
              </div>
              <dl className="ret__treaty-details">
                <div className="ret__treaty-row">
                  <dt>Reinsurer</dt>
                  <dd>{t.reinsurer}</dd>
                </div>
                <div className="ret__treaty-row">
                  <dt>Retention</dt>
                  <dd>{formatCurrency(t.retention)}</dd>
                </div>
                <div className="ret__treaty-row">
                  <dt>Ceded %</dt>
                  <dd>{t.cededPct}%</dd>
                </div>
                <div className="ret__treaty-row">
                  <dt>Effective Period</dt>
                  <dd>{formatDate(t.effectiveStart)} – {formatDate(t.effectiveEnd)}</dd>
                </div>
              </dl>
            </div>
          ))}
        </div>
      </div>

      {/* Bordereaux section */}
      <div className="ret__section">
        <h2 className="ret__section-title">Bordereaux Generation</h2>
        <div className="ret__bord-wrap card">
          <table className="ret__bord-table">
            <thead>
              <tr>
                <th>Treaty</th>
                <th>Period</th>
                <th>Status</th>
                <th>Due Date</th>
              </tr>
            </thead>
            <tbody>
              {MOCK_BORDEREAUX.map((b) => (
                <tr key={b.id} className="ret__bord-row">
                  <td className="ret__bord-treaty">{b.treaty}</td>
                  <td>{b.period}</td>
                  <td>
                    <span className={getBordereauStatusClass(b.status)}>{b.status}</span>
                  </td>
                  <td className="ret__bord-date">{formatDate(b.dueDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Panel participation */}
      <div className="ret__section">
        <h2 className="ret__section-title">Panel Participation</h2>
        <div className="ret__panel-wrap card">
          <table className="ret__panel-table">
            <thead>
              <tr>
                <th>Reinsurer</th>
                <th>Share %</th>
                <th>Premium</th>
              </tr>
            </thead>
            <tbody>
              {MOCK_PANEL.map((p, idx) => (
                <tr key={idx} className="ret__panel-row">
                  <td className="ret__panel-reinsurer">{p.reinsurer}</td>
                  <td className="ret__panel-share">{p.sharePct}%</td>
                  <td className="ret__panel-premium">{formatCurrency(p.premium)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
